import { FunctionComponent, useMemo } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  addedProduct,
  addToCartAtom,
  cartAtom,
  openCartToggle,
} from '../../../state/cart';
import { useAtom } from 'jotai';
import addToCartFetch from '../../../services/cart/add-to-cart';
import { viewSelection } from '../../../state/selection';
import ListCard from './list-card';
import ColumnCard from './column-card';
import { useRouter } from 'next/router';
import { getCartKeys } from '../../../utils/react-query/query-keys';
import { useAppContext } from '../../providers/app-context';

const ProductCard: FunctionComponent<{
  item: TProductListItem;
  actState: 'ADDED' | undefined;
  index?: number;
  view?: 'DESKTOP' | 'MOBILE';
}> = ({ item, actState, index = 0, view }) => {
  const {
    id,
    sku,
    part,
    car_brand,
    car_model,
    car_version,
    reference,
    pricing,
    category_en_old,
    category_en,
    part_en,
  } = item;
  const { locale = 'en', pathname } = useRouter();
  const [, toggleCart] = useAtom(openCartToggle);
  const [, addToCart] = useAtom(addToCartAtom);
  const [, setAddedProduct] = useAtom(addedProduct);
  const queryClient = useQueryClient()
  const [shoppingCart, setShoppingCart] = useAtom(cartAtom);
  const { detailedPrice } = useAppContext();

  const isAdvancedSearch = useMemo(
    () => pathname.includes('advanced-search'),
    [pathname]
  );

  const { mutateAsync: addToCartMutate, isLoading: addingProduct } =
    useMutation(addToCartFetch, {
      onSuccess: (data) => {
        // update cartAtom to display the correct number of items on header
        // this will also call a useEffect on the product page see: shoppingCart
        addToCart(id);
        setAddedProduct(id);
        setShoppingCart(
          data.products.map((product) => {
            return product.id;
          })
        );
        queryClient.resetQueries(getCartKeys({ locale, isAdvancedView: isAdvancedSearch, detailedPrice: detailedPrice || false }));
        //queryClient.setQueryData(getCartKeys({ locale, isAdvancedView: false, detailedPrice: detailedPrice || false }), data);
        toggleCart();

        window.dataLayer.push({
          event: 'eec.add',
          ecommerce: {
            currencyCode: 'EUR', // currency code
            add: {
              products: [
                {
                  id: sku, // SKU of the product
                  name: part, // name of the product.
                  brand: car_brand, // brand of the part
                  category: category_en_old, // Product category(ies) - each "/" is a category level (max 5 levels)
                  price: Number(pricing?.price_product).toFixed(2), // price of the part without shipping, tax and currency
                  quantity: 1, // quantity added to the cart
                  dimension2: reference, // reference of the product
                  dimension3: car_model, // car model of the part
                  dimension4: car_version, // car version of the part
                },
              ],
            },
          },
        });

        // GA4
        window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
        window.dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            currency: 'EUR',
            items: [
              {
                item_name: part,
                item_id: sku,
                price: Number(pricing?.price_product).toFixed(2),
                item_brand: car_brand,
                item_category: category_en,
                item_category2: part_en,
                //item_list_name: 'Search Results',
                //item_list_id: 'search-results',
                index: 0, // hardcoded to zero in this event
                quantity: '1',
              },
            ],
          },
        });
      },
    });

  return (
    <>
      {view === 'DESKTOP' ? (
        <ListCard
          item={item}
          actState={actState}
          addToCartMutate={addToCartMutate}
          view={view}
        ></ListCard>
      ) : (
        <ColumnCard
          item={item}
          actState={actState}
          addToCartMutate={addToCartMutate}
          view={view}
        ></ColumnCard>
      )}
    </>
  );
};

export default ProductCard;
